// Theme primitive — D-38 + D-11.
// Reads localStorage pre-auth; on user signed in, fetches public.user_preferences
// and applies. On every change, writes both localStorage and (Phase 2) the supabase row.
// Sets <html data-mode="dark|light"> + data-brand-feel + data-serif attributes.
// Listens to matchMedia('(prefers-color-scheme: dark)') when mode=system.
import type { SupabaseClient } from "@supabase/supabase-js"
import { useSyncExternalStore } from "react"
import type { AuthPrimitive } from "./auth"

type BrandFeel = "editorial-tech" | "editorial-classic" | "linear-tight" | "notion-soft"
type Serif = "newsreader" | "source-serif" | "lora" | "inter-only"
type Mode = "dark" | "light" | "system"

interface State {
  brandFeel: BrandFeel
  serif: Serif
  mode: Mode
}

const LS_KEY = "readose.theme.v1"

const DEFAULTS: State = { brandFeel: "editorial-tech", serif: "newsreader", mode: "system" }

export class Theme {
  #state: State = DEFAULTS
  #listeners = new Set<() => void>()
  #mediaQuery: MediaQueryList | null = null
  readonly #supabase: SupabaseClient

  constructor(supabase: SupabaseClient, _auth: AuthPrimitive) {
    this.#supabase = supabase
    this.#hydrateFromLocalStorage()
    this.#applyToDom()
    this.#wireSystemMediaQuery()
  }

  async hydrateFromServer(): Promise<void> {
    const { data: u } = await this.#supabase.auth.getUser()
    if (!u.user) return
    const { data: pref } = await this.#supabase
      .from("user_preferences")
      .select("brand_feel, serif, color_mode")
      .eq("user_id", u.user.id)
      .maybeSingle()
    if (pref) {
      this.#state = {
        brandFeel: pref.brand_feel as BrandFeel,
        serif: pref.serif as Serif,
        mode: pref.color_mode as Mode,
      }
      this.#persistLocal()
      this.#applyToDom()
      this.#notify()
    } else {
      // No row yet — INSERT with current localStorage values (Pitfall 7 mitigation).
      await this.#supabase.from("user_preferences").insert({
        user_id: u.user.id,
        brand_feel: this.#state.brandFeel,
        serif: this.#state.serif,
        color_mode: this.#state.mode,
      })
    }
  }

  setBrandFeel(b: BrandFeel): void {
    this.#state = { ...this.#state, brandFeel: b }
    this.#after()
  }
  setSerif(s: Serif): void {
    this.#state = { ...this.#state, serif: s }
    this.#after()
  }
  setMode(m: Mode): void {
    this.#state = { ...this.#state, mode: m }
    this.#after()
  }

  useTheme(): State {
    return this.#useStore((s) => s)
  }
  useBrandFeel(): BrandFeel {
    return this.#useStore((s) => s.brandFeel)
  }
  useSerif(): Serif {
    return this.#useStore((s) => s.serif)
  }
  useMode(): Mode {
    return this.#useStore((s) => s.mode)
  }

  #after(): void {
    this.#persistLocal()
    this.#applyToDom()
    this.#wireSystemMediaQuery()
    this.#notify()
    // Phase 2 wires this to apps/api or supabase row update.
    void this.#supabase.auth.getUser().then(async ({ data }) => {
      if (data.user) {
        await this.#supabase.from("user_preferences").upsert({
          user_id: data.user.id,
          brand_feel: this.#state.brandFeel,
          serif: this.#state.serif,
          color_mode: this.#state.mode,
        })
      }
    })
  }

  #applyToDom(): void {
    const html = document.documentElement
    html.dataset.brandFeel = this.#state.brandFeel
    html.dataset.serif = this.#state.serif
    const effectiveMode: "dark" | "light" =
      this.#state.mode === "system"
        ? window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : this.#state.mode
    html.dataset.mode = effectiveMode
  }

  #wireSystemMediaQuery(): void {
    if (this.#mediaQuery) {
      this.#mediaQuery.removeEventListener("change", this.#systemHandler)
      this.#mediaQuery = null
    }
    if (this.#state.mode === "system") {
      this.#mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
      this.#mediaQuery.addEventListener("change", this.#systemHandler)
    }
  }
  #systemHandler = (): void => {
    this.#applyToDom()
  }

  #hydrateFromLocalStorage(): void {
    try {
      const raw = window.localStorage.getItem(LS_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as Partial<State>
      this.#state = { ...DEFAULTS, ...parsed }
    } catch {
      /* ignore */
    }
  }

  #persistLocal(): void {
    try {
      window.localStorage.setItem(LS_KEY, JSON.stringify(this.#state))
    } catch {
      /* ignore */
    }
  }

  #useStore<T>(selector: (s: State) => T): T {
    // biome-ignore lint/correctness/useHookAtTopLevel: class-method-exposed hook intended for use inside React components (D-38 Theme primitive)
    return useSyncExternalStore(
      (cb) => {
        this.#listeners.add(cb)
        return () => {
          this.#listeners.delete(cb)
        }
      },
      () => selector(this.#state),
      () => selector(this.#state),
    )
  }

  #notify(): void {
    for (const cb of this.#listeners) cb()
  }
}
